import React from 'react';
import MappingFlow from './MappingFlow';

/**
 * ConfigPageShell — page chrome shared by every Configuration page.
 *
 * One head band on a single line (wraps on narrow):
 *   h1 (serif) · subtitle · ink-pill tabs · [right: scope selector · actions]
 * then the MappingFlow rail (optional), then `children` (normally an it-frame).
 *
 * props
 *   title, subtitle
 *   tabs        [{ key, label, count?, warn? }]   count renders as a mono pill; warn=true tints it
 *   activeTab, onTabChange(key)
 *   scope       node — location/org selector (use ScopeSelect below)
 *   actions     node — head-level actions (rare; most actions live in the toolbar)
 *   flow        MappingFlow props (stations, coverage, onUnmappedClick) — omitted = no rail
 *   children
 */
export default function ConfigPageShell({
  title,
  subtitle,
  tabs,
  activeTab,
  onTabChange,
  scope,
  actions,
  flow,
  children,
  className,
}) {
  return (
    <div className={`cp-page${className ? ` ${className}` : ''}`}>
      <header className="cp-head">
        <h1>{title}</h1>
        {subtitle ? <div className="cp-sub">{subtitle}</div> : null}
        {Array.isArray(tabs) && tabs.length > 1 ? (
          <div className="cp-tabs" role="tablist" aria-label={`${title} sections`}>
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={activeTab === t.key}
                className={activeTab === t.key ? 'cp-active' : ''}
                onClick={() => onTabChange && onTabChange(t.key)}
              >
                {t.label}
                {t.count != null ? (
                  <span className={`cp-tab-count${t.warn ? ' cp-warn' : ''}`}>{t.count}</span>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
        {(scope || actions) ? (
          <div className="cp-head-right">
            {scope}
            {actions}
          </div>
        ) : null}
      </header>
      {flow ? <MappingFlow {...flow} /> : null}
      {children}
    </div>
  );
}

/**
 * ScopeSelect — the location (or any scope) picker for the head band.
 * Wraps a kit InlineSelect with the small-caps label so every page places
 * and labels it identically.
 */
export function ScopeSelect({ label = 'Location', children }) {
  return (
    <div className="cp-scope">
      <span className="cp-scope-label">{label}</span>
      {children}
    </div>
  );
}
